import React from 'react';
import { trackWidgetEvent } from '../../utils/analytics';
import { getIsMobile } from '../../utils/device';
import {
  INTAKE_FIELDS_REQUIRED,
  CHAT_WIDGET_INTAKE_FORM_SHOWN,
  CHAT_WIDGET_INTAKE_FIELD_COMPLETED,
  CHAT_WIDGET_INTAKE_COUNTRY_CODE_CHANGED,
  CHAT_WIDGET_INTAKE_VALIDATION_ERROR,
  CHAT_WIDGET_INTAKE_FORM_SUBMITTED,
  CHAT_WIDGET_INTAKE_FORM_ABANDONED,
  CHAT_WIDGET_INTAKE_NO_LAST_NAME_DECLARED,
} from './intake-copy';
import type { IntakeFormValues } from './_sections/IntakeFields';
import type { IntakeFieldName } from './intake-validation';

export function useIntakeAnalytics(
  sessionId: string,
  prefillIso: string | null
) {
  const startedAt = React.useRef(0);
  const attemptCounts = React.useRef<Record<string, number>>({});
  const lastTouched = React.useRef<string | null>(null);
  const submitted = React.useRef(false);
  const validationErrorCount = React.useRef(0);
  const shownFired = React.useRef(false);

  React.useEffect(() => {
    if (startedAt.current === 0) {
      startedAt.current = Date.now();
    }
  }, []);

  React.useEffect(() => {
    if (shownFired.current) return;
    shownFired.current = true;
    trackWidgetEvent(CHAT_WIDGET_INTAKE_FORM_SHOWN, {
      sessionId,
      variant: 'form_on_open',
      fieldsRequired: [...INTAKE_FIELDS_REQUIRED],
      deviceType: getIsMobile() ? 'mobile' : 'desktop',
      language: typeof navigator !== 'undefined' ? navigator.language : null,
      ipCountryResolved: prefillIso,
    });
  }, [sessionId, prefillIso]);

  function trackFieldCompleted(field: IntakeFieldName) {
    lastTouched.current = field;
    attemptCounts.current[field] = (attemptCounts.current[field] ?? 0) + 1;
    trackWidgetEvent(CHAT_WIDGET_INTAKE_FIELD_COMPLETED, {
      sessionId,
      fieldName: field,
      attemptCount: attemptCounts.current[field],
    });
  }

  function trackDialOverride(
    ipCountry: string | null,
    selectedCountry: string
  ) {
    trackWidgetEvent(CHAT_WIDGET_INTAKE_COUNTRY_CODE_CHANGED, {
      sessionId,
      ipCountry,
      selectedCountry,
    });
  }

  function trackNoFamilyNameDeclared(countryOfResidence: string) {
    trackWidgetEvent(CHAT_WIDGET_INTAKE_NO_LAST_NAME_DECLARED, {
      sessionId,
      countryOfResidence: countryOfResidence || null,
    });
  }

  function trackValidationErrors(errors: Record<string, string>) {
    validationErrorCount.current += Object.keys(errors).length;
    for (const [fieldName, message] of Object.entries(errors)) {
      trackWidgetEvent(CHAT_WIDGET_INTAKE_VALIDATION_ERROR, {
        sessionId,
        fieldName,
        errorType: /did you mean/i.test(message) ? 'email_typo' : 'invalid',
      });
    }
  }

  function trackSubmitted(opts: {
    consentGiven: boolean;
    countryCodeOverridden: boolean;
  }) {
    submitted.current = true;
    trackWidgetEvent(CHAT_WIDGET_INTAKE_FORM_SUBMITTED, {
      sessionId,
      timeToSubmitMs: Date.now() - startedAt.current,
      consentGiven: opts.consentGiven,
      validationErrorCount: validationErrorCount.current,
      countryCodeOverridden: opts.countryCodeOverridden,
      deviceType: getIsMobile() ? 'mobile' : 'desktop',
    });
  }

  function trackAbandoned(values: IntakeFormValues) {
    if (submitted.current) return;
    const fieldsCompletedCount = [
      values.givenName.trim(),
      values.noFamilyName || values.familyName.trim(),
      values.email.trim(),
      Boolean(values.phoneCountryIso.trim()) &&
        values.phone.replace(/\D/g, '').length >= 8,
      values.currentCountryCode.trim(),
    ].filter(Boolean).length;
    trackWidgetEvent(CHAT_WIDGET_INTAKE_FORM_ABANDONED, {
      sessionId,
      fieldsCompletedCount,
      lastFieldTouched: lastTouched.current,
      timeOnFormMs: Date.now() - startedAt.current,
    });
  }

  return {
    submitted,
    trackFieldCompleted,
    trackDialOverride,
    trackNoFamilyNameDeclared,
    trackValidationErrors,
    trackSubmitted,
    trackAbandoned,
  };
}
